import { useState, useRef, useCallback, useEffect } from "preact/hooks";
import { useCustomizerState } from "./use-customizer-state";
import { InspectTab } from "./InspectTab";

export function CSSCustomizer() {
  const [open, setOpen] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [exportCopied, setExportCopied] = useState(false);

  const {
    state,
    setClassProperty,
    resetClassProperty,
    resetAll,
    generateExport,
    hasChanges,
  } = useCustomizerState();

  // Drag
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
  } | null>(null);

  const onDragStart = useCallback((e: MouseEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    const panel = panelRef.current;
    if (!panel) return;
    e.preventDefault();

    const rect = panel.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: rect.left,
      startTop: rect.top,
    };

    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      let left = dragRef.current.startLeft + dx;
      let top = dragRef.current.startTop + dy;
      left = Math.max(0, Math.min(left, window.innerWidth - 200));
      top = Math.max(0, Math.min(top, window.innerHeight - 100));
      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
      panel.style.right = "auto";
      panel.style.bottom = "auto";
    };

    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, []);

  // Resize
  const resizeRef = useRef<{
    startX: number;
    startY: number;
    startW: number;
    startH: number;
  } | null>(null);

  const onResizeStart = useCallback((e: MouseEvent) => {
    const panel = panelRef.current;
    if (!panel) return;
    e.preventDefault();
    e.stopPropagation();

    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: panel.offsetWidth,
      startH: panel.offsetHeight,
    };

    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const dw = ev.clientX - resizeRef.current.startX;
      const dh = ev.clientY - resizeRef.current.startY;
      const w = Math.max(300, resizeRef.current.startW + dw);
      const h = Math.max(200, resizeRef.current.startH + dh);
      panel.style.width = `${w}px`;
      panel.style.maxHeight = `${h}px`;
    };

    const onUp = () => {
      resizeRef.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, []);

  // Close export on Escape
  useEffect(() => {
    if (!showExport) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowExport(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showExport]);

  const handleCopyExport = () => {
    navigator.clipboard.writeText(generateExport()).then(() => {
      setExportCopied(true);
      setTimeout(() => setExportCopied(false), 1500);
    });
  };

  return (
    <div className="festapp-customizer">
      {open && (
        <div ref={panelRef} className="festapp-customizer-panel">
          {/* Header */}
          <div className="festapp-customizer-header" onMouseDown={onDragStart}>
            <span className="festapp-customizer-title">CSS Customizer</span>
            <div className="festapp-customizer-header-actions">
              {hasChanges && (
                <button
                  type="button"
                  className="festapp-customizer-header-btn"
                  onClick={resetAll}
                >
                  Reset
                </button>
              )}
              <button
                type="button"
                className="festapp-customizer-header-btn festapp-customizer-export-btn"
                onClick={() => {
                  setShowExport(true);
                  setExportCopied(false);
                }}
                disabled={!hasChanges}
              >
                Export
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="festapp-customizer-body">
            <InspectTab
              active={open}
              classOverrides={state.classOverrides}
              onSetClassProperty={setClassProperty}
              onResetClassProperty={resetClassProperty}
            />
          </div>

          {/* Resize handle */}
          <div
            className="festapp-customizer-resize"
            onMouseDown={onResizeStart}
          />
        </div>
      )}

      {/* Export overlay */}
      {showExport && (
        <div
          className="festapp-customizer-export-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowExport(false);
          }}
        >
          <div className="festapp-customizer-export-modal">
            <div className="festapp-customizer-export-header">
              <span>Export CSS</span>
              <button
                type="button"
                className="festapp-customizer-export-close"
                onClick={() => setShowExport(false)}
              >
                &times;
              </button>
            </div>
            <textarea
              className="festapp-customizer-export-textarea"
              readOnly
              value={generateExport()}
            />
            <button
              type="button"
              className="festapp-customizer-export-copy"
              onClick={handleCopyExport}
            >
              {exportCopied ? "Copied!" : "Copy to Clipboard"}
            </button>
          </div>
        </div>
      )}

      {/* Toggle FAB */}
      <div className="festapp-customizer-controls">
        <button
          className={`festapp-customizer-toggle ${open ? "festapp-customizer-toggle-active" : ""}`}
          onClick={() => {
            setOpen(!open);
            if (open) setShowExport(false);
          }}
          title="CSS Customizer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
          {open ? "Close" : "CSS Customizer"}
        </button>
      </div>
    </div>
  );
}
